import { type Draft } from 'immer';

import { type ElementsState, type EngineStateBase } from '@blaze-runtime/engine-data-access';
import {
  type CanvasElement,
  type CanvasElementId,
  type CanvasElementType,
  type ElementTargetPath,
  type SlotAlignment,
  type WrapperRefs,
} from '@blaze-runtime/engine-domain';
import {
  applyPropsPatch,
  findByRefIds,
  SEARCH_AND_FILTERS_ELEMENT_SLOT_ID,
  setWrapperRefMutably,
  WrapperActionButton,
  WrapperConfirmButton,
  WrapperField,
  WrapperFilter,
  WrapperSearch,
} from '@blaze-runtime/engine-domain';
import {
  type CanvasElementPropertiesWithData,
  mergeDataBindingsValues,
} from '@blaze-shared/elements-domain';
import { deepmergeExceptArrays } from '@blaze-shared/shared-util';
import { type Workflow } from '@blaze-shared/workflow-domain';

import { type CanvasElementDefinitionInternal } from '../../canvas-elements/element-definition';
import { stripClassTypes } from '../../canvas-elements/element-definition';
import {
  hasDataProps,
  hasProps,
  hasSearchAndFilters,
  hasSlots,
  hasWrapperRefs,
} from '../../canvas-elements/element-model/element-props-guards';
import {
  isDataFilter,
  isElementType,
  isFormList,
  isVariantOfFormContainer,
} from '../../canvas-elements/element-model/element-type-guards';
import { getDataSourceInputName } from '../../canvas-elements/element-model/io';
import { getWrapperRefs } from '../../canvas-elements/element-model/wrapper-refs';
import { type FieldInfo } from '../../canvas-elements/field-info';
import { getContainerDefinition } from '../../elements/container/container.definition';
import { type FormElement } from '../../elements/form-container/form-container.model';
import {
  WRAPPER_HEADER_SLOT_ID,
  WRAPPER_MAIN_ELEMENT_SLOT_ID,
} from '../../elements/wrapper/wrapper.models';
import { findWrapperForElement, getElementPath } from '../../utils/canvas-element/path';
import { getPathBeforeElement, getPathLastInSlot } from '../../utils/layout/helpers';
import { insertColumnMutably } from '../../utils/layout/insert-column';
import { placeElementMutably } from '../../utils/layout/place-element';
import {
  removeColumnMutably,
  removeElementFromParentMutably,
  removeElementMutably,
} from '../../utils/layout/remove-element';

/**
 * Experimental accessor function.
 * Prevent OOM in IntelliJ IDE
 * @param draft
 * @param elementId
 */
function getElement<T extends CanvasElement = CanvasElement>(
  draft: Draft<ElementsState>,
  elementId: CanvasElementId | undefined,
): T | undefined {
  return draft.entities[elementId || ''] as T | undefined;
}

/** The element changes a generation committed, as a diff over the graph. */
export interface ElementsDiff {
  elementIdsToRemove: CanvasElementId[];
  elementsToUpsert: CanvasElement[];
}

/**
 * Applies a generation's diff: the elements it dropped, then the ones it wrote.
 *
 * Generations commit a diff rather than a whole entity state so an editor write they never saw
 * cannot be silently reverted (BL-9502); this is the reducer half of that contract, shared by
 * every generation that speaks it.
 *
 * **Removals come first.** `removeElementMutably` unlinks each element from its parent, so it
 * has to run while the parents still look the way the element remembers - upserting first
 * would install parents that have already forgotten the child (a whole removed slot, say) and
 * leave the unlink with nothing to find. The upserts are authoritative either way: they were
 * computed from the same pass as the removals, so they already reflect them.
 */
export function applyElementsDiffMutably(
  draft: Draft<EngineStateBase>,
  { elementsToUpsert, elementIdsToRemove }: ElementsDiff,
): void {
  for (const elementId of elementIdsToRemove) {
    removeElementMutably(draft, elementId);
  }

  for (const element of elementsToUpsert) {
    draft.entities[element.id] = element;
  }
  // why: `ids` is an index over `entities`, so rebuilding it from the dictionary keeps the two
  // in step without having to tell a new element from an updated one.
  draft.ids = Object.keys(draft.entities);
}

/**
 * Removes unused refs and adjusts the properties
 * @param draft proxy state
 * @param elementId the element to be sanitized
 */
export function sanitizeWrapperRefsMutably(
  draft: Draft<ElementsState>,
  elementId: CanvasElementId,
): void {
  // Find missing refs
  const element = getElement(draft, elementId);
  if (!hasWrapperRefs(element)) {
    return;
  }

  const refs = getWrapperRefs(element);

  Object.entries(refs).forEach(([refId, refElementId]) => {
    if (getElement(draft, refElementId)) {
      return;
    } // Referenced element is not missing

    // Search and Filters
    if (hasSearchAndFilters(element)) {
      // Adjust the element's props to reflect the change
      if (WrapperSearch.isRefId(refId)) {
        element.props.content.searchAndFilters.searchEnabled = false;
      } else if (WrapperFilter.isRefId(refId)) {
        // Remove the filter for missing ref from the list
        const filters = element.props.content.searchAndFilters.filters;
        element.props.content.searchAndFilters.filters = filters?.filter(
          (filter) => !WrapperFilter.getRefIds(String(filter.id)).includes(refId),
        );
      }
    }

    // Form/Details Model
    if (isVariantOfFormContainer(element) || isFormList(element)) {
      if (WrapperField.isRefId(refId)) {
        const model = element.props.content.model;
        if (!model) {
          throw new Error('Form model missing');
        }

        // If field is present in the model - set its visibility to false
        const item = (
          model as Pick<(typeof model)[number], 'field' | 'fieldId' | 'visible' | 'conditions'>[]
        ).find((item) => WrapperField.getRefIds(item.field, item.fieldId).includes(refId));
        if (item) {
          item.visible = false;
          item.conditions = undefined;
        }
      } else if (WrapperActionButton.isRefId(refId)) {
        const content = (element as FormElement).props.content;
        if (!content?.formActions) {
          throw new Error('Form actions model missing');
        }

        // remove the action
        const actionId = WrapperActionButton.getValue(refId);
        content.formActions = content.formActions.filter((fa) => fa.id !== actionId);
      }
    }

    // Remove the ref
    delete refs[refId];
  });
}

const ELEMENT_INJECTION_SUPPORT: CanvasElementType[] = [
  'ganttChart',
  'dataFilter',
  'kanbanBoard',
  'list',
  'map',
  'table',
];

/**
 * Used for Search & Filters feature for Get dataset sources
 * and for API Call wrapper for API Calls as Data Source.
 */
export function updateFiltersMutably(
  draft: Draft<EngineStateBase>,
  elementId: CanvasElementId,
  fields: FieldInfo[],
  wrapperDefinition: CanvasElementDefinitionInternal,
  inputWorkflow: Workflow,
): void {
  const mainElement = getElement(draft, elementId);
  if (!mainElement || !hasProps(mainElement)) {
    throw new Error('Incorrect element');
  }

  const hasFields = fields.length > 0;
  const isElementInjectionSupported = ELEMENT_INJECTION_SUPPORT.includes(mainElement.type);

  // Find wrapper
  let wrapperElementId = findWrapperForElement(draft, elementId);

  // If wrapper present and all fields have been removed - remove the wrapping
  if (!hasFields && wrapperElementId) {
    unwrapElementMutably(draft, elementId);
    return;
  }

  // If no fields are present and no refs to cleanup - do nothing
  if (!hasFields && !hasWrapperRefs(mainElement)) {
    return;
  }

  // Determine if search and filters should change its injection point
  // and move if necessary
  if (isElementInjectionSupported && shouldChangeInjectionMethod(draft, elementId, fields)) {
    mainElement.props = applyPropsPatch(mainElement.props, {
      content: { searchAndFilters: { injectToElement: true } },
    }) as typeof mainElement.props;
    moveSearchAndFiltersToElementSlotMutably(draft, elementId);
  }

  const injectToElement = shouldInjectFieldsToElement(mainElement);

  if (injectToElement) {
    // Clear filter column when no fields are present
    if (fields.length === 0) {
      const children = mainElement?.slots?.[SEARCH_AND_FILTERS_ELEMENT_SLOT_ID]?.children;

      children?.forEach((id) => {
        removeElementMutably(draft, id);
      });

      // Clean wrapper refs
      delete mainElement.props.wrapper;

      return;
    }

    // Initialize wrapper refs props
    if (!hasWrapperRefs(mainElement)) {
      mainElement.props.wrapper = { refs: {} };
    }
  } else if (!wrapperElementId) {
    // If wrapper missing - create one
    // Store position
    const currentPath = getElementPath(draft.entities, elementId);
    if (!currentPath) {
      throw new Error(`Could not identify element's path`);
    }
    currentPath.targetPosition = { type: 'left' };

    // Remove before placing a wrapper
    removeElementFromParentMutably(draft, elementId);

    // Add container with element inside (make sure adding directly after 'removing for dragging' won't break anything)
    // Place existing element
    const result = placeElementMutably(draft, {
      elementDefinition: wrapperDefinition,
      atPath: currentPath,
    });
    if (!result?.elementId) {
      throw new Error('Could not add wrapper');
    }

    wrapperElementId = result.elementId;
    const wrapperElement = getElement(draft, wrapperElementId);
    if (!hasSlots(wrapperElement) || !hasProps(wrapperElement)) {
      throw new Error('Newly created wrapper error');
    }
    wrapperElement.width = mainElement.width;
    wrapperElement.height = mainElement.height;
    wrapperElement.props = deepmergeExceptArrays(
      wrapperElement.props as any,
      {
        wrapper: { mainElementId: elementId },
      } as any,
    );

    const initialSlot = wrapperElement.slots[WRAPPER_MAIN_ELEMENT_SLOT_ID];
    if (!initialSlot) {
      throw new Error('Could not identify initial slot');
    }
    initialSlot.children.push(elementId);
    initialSlot.props = deepmergeExceptArrays(initialSlot.props, {
      style: { container: { offset: { padding: '' } } },
    });

    // Initialize wrapper refs props
    mainElement.props.wrapper = { refs: {} };
    mainElement.parentNode = { id: wrapperElementId, slot: WRAPPER_MAIN_ELEMENT_SLOT_ID };

    // Make element use full width of the wrapper
    mainElement.width = { mode: 'full' };
    mainElement.height = { mode: 'full' };

    // Select wrapper element
    draft.selectedPathNode = { id: wrapperElementId };
  }

  // Remove unused elements
  const wrapperRefs = getWrapperRefs(mainElement);

  // Create container for the fields
  let containerRef = wrapperRefs['container'];

  // Remove elements for fields that are no longer needed
  Object.entries(wrapperRefs).forEach(([fieldRefId, fieldElementId]) => {
    // If is ref is decorator skip remove
    if (fieldRefId === 'container') {
      // Remove container ref if it points to nowhere
      if (!getElement(draft, fieldElementId)) {
        delete wrapperRefs[fieldRefId];
        removeElementMutably(draft, fieldElementId);
        containerRef = '';
      }

      return;
    }

    if (!fields.some((field) => field.refIds.includes(fieldRefId))) {
      delete wrapperRefs[fieldRefId];
      removeElementMutably(draft, fieldElementId);
    }
    const field = fields.find((field) => field.refIds.includes(fieldRefId));
    if (field?.elementTypeChangedFieldId !== undefined) {
      delete wrapperRefs[fieldRefId];
      removeElementMutably(draft, fieldElementId);
      removeFieldTypeChangedMarkerMutably(mainElement, field.elementTypeChangedFieldId);
    }
  });

  // If container missing and new fields added, container needs to be regenerated
  // If the elements have been taken out of the container and no new fields are added - the container can be removed by the user.
  // Add container
  if (!containerRef && hasNewFields(wrapperRefs, fields)) {
    let containerPath: ElementTargetPath;
    if (injectToElement) {
      // Add slot if necessary
      if (isElementInjectionSupported) {
        addSearchAndFilterElementSlotMutably(mainElement);
      } else {
        throw new Error(`Unsupported element type: ${mainElement.type}`);
      }

      containerPath = {
        id: mainElement.id,
        parentId: mainElement.id,
        slot: SEARCH_AND_FILTERS_ELEMENT_SLOT_ID,
        index: 0,
        targetPosition: { type: 'top' },
      };
    } else {
      const wrapperElement = getElement(draft, wrapperElementId);
      if (!wrapperElement) {
        throw new Error('Wrapper not found');
      }
      containerPath = {
        id: wrapperElement.id,
        parentId: wrapperElement.id,
        slot: WRAPPER_HEADER_SLOT_ID,
        index: 0,
        targetPosition: { type: 'top' },
      };
    }

    const containerDefinition = stripClassTypes(getContainerDefinition());

    const result = placeElementMutably(draft, {
      elementDefinition: containerDefinition,
      atPath: containerPath,
    });
    if (!result?.elementId) {
      throw new Error('Could not add container');
    }

    containerRef = result.elementId;
    wrapperRefs['container'] = containerRef;
  }

  if (containerRef) {
    createContainersForFieldsMutably(draft, containerRef, fields, wrapperRefs, mainElement.type);

    for (const field of fields) {
      let fieldRef = findByRefIds(wrapperRefs, field.refIds);

      // If no ref - add field
      if (!fieldRef) {
        const fieldContainer = getElement(draft, containerRef);
        if (!hasSlots(fieldContainer) || !hasProps(fieldContainer)) {
          throw new Error('Container error');
        }

        const slotId =
          field.type === 'search'
            ? fieldContainer.slots.ids[0]
            : fieldContainer.slots.ids[fieldContainer.slots.ids.length - 1];

        // Calculate path for the new field
        let atPath = getPathLastInSlot(draft, containerRef, slotId);
        // If has a confirm button - place new field before the button
        const confirmButtonRef = findByRefIds(wrapperRefs, WrapperConfirmButton.getRefIds());
        if (confirmButtonRef) {
          const confirmButtonPath = getElementPath(draft.entities, confirmButtonRef);
          // If confirm button present inside of the generated container (as it was generated) - put the new field before it
          if (confirmButtonPath?.parentId === containerRef) {
            atPath = getPathBeforeElement(draft, confirmButtonRef);
          }
        }

        const result = placeElementMutably(draft, {
          elementDefinition: field.elementDefinition,
          atPath,
          initProps: field.initProps,
        });
        if (!result?.elementId) {
          throw new Error('Failed to place the field');
        }

        // Add reference to the field
        fieldRef = result.elementId;
        setWrapperRefMutably(wrapperRefs, field.refIds, result.elementId);

        // Configure element
        const fieldElement = getElement(draft, fieldRef);
        if (!hasProps(fieldElement)) {
          throw new Error('Field element error');
        }

        const noLabel = { content: { title: { enabled: false } } } as any;
        fieldElement.props = deepmergeExceptArrays(fieldElement.props as any, noLabel);

        // Set field type specific settings
        if (field.type === 'filter') {
          fieldElement.width = { mode: 'value', fixed: true, value: 152 };
        } else if (field.type === 'search') {
          fieldElement.width = { mode: 'value', fixed: true, value: 238 };
        }
        fieldElement.height = { mode: 'value', fixed: !isDataFilter(mainElement), value: 48 };
      }

      const fieldElement = getElement(draft, fieldRef);
      if (!hasProps(fieldElement)) {
        throw new Error('Field element error');
      }

      // Update props
      if (field.updateProps) {
        const dataUpdatePatch = 'data' in field.updateProps ? field.updateProps.data : undefined;
        const updatePropsWithoutData = { ...(field.updateProps as any) };
        delete updatePropsWithoutData.data;
        const currentData = hasDataProps(fieldElement) ? fieldElement.props.data : undefined;

        fieldElement.props = deepmergeExceptArrays(
          fieldElement.props as any,
          updatePropsWithoutData as any,
        );

        // Update data bindings without losing existing entries (for example `visible`).
        if (dataUpdatePatch) {
          if (!hasDataProps(fieldElement)) {
            (fieldElement.props as CanvasElementPropertiesWithData).data = [];
          }
          if (hasDataProps(fieldElement)) {
            fieldElement.props.data = mergeDataBindingsValues(currentData, dataUpdatePatch);
          }
        }
      }
    }

    const fieldContainer = getElement(draft, containerRef);
    if (isElementType(fieldContainer, 'container')) {
      fieldContainer.slots.ids.forEach((id) => {
        const slot = fieldContainer.slots[id];
        if (slot && !slot?.children.length) {
          removeColumnMutably(draft, containerRef, id);
        }
      });

      /**
       * When removing all columns from container
       * entire element is removed.
       * After that wrapper ref needs to be removed from main element
       */
      if (!getElement(draft, fieldContainer.id) && wrapperRefs['container']) {
        delete wrapperRefs['container'];
      }
    }
  }

  // Update data source to context
  const mainElementWithDataProps = getElement(draft, elementId);
  if (!hasDataProps(mainElementWithDataProps)) {
    throw new Error('Missing data props');
  }
  const valueBinding = mainElementWithDataProps.props.data.find(
    (data) => data?.key === getDataSourceInputName((mainElement as CanvasElement).type),
  );
  if (!valueBinding) {
    throw new Error('Missing value binding');
  }

  // Ensure value is bound to a workflow (fresh workflow or converted from 'dataset' source type)
  if (valueBinding.dataSource?.sourceType === 'dataset') {
    valueBinding.dataSource.sourceType = 'context';
    valueBinding.dataSource.id = inputWorkflow.id;
  }

  // Reorder
  // TODO: Reorder if needed
  // ensurePositionInSet(draft, [allNonSearchFieldIds], fieldElementId, 3);
  // or: ensurePositionInSet(draft, [allNonSearchFieldIdsInOrder]); -> finds them all and replaces them in their position in the correct order
  // Find all ElementTargetPaths, removeAndPlaceThemBack

  // 1. Store previous field order + ElementTargetPath (has to be done before adding)
  // 2. Add new ones to the list
  // 3. Replace if the order got changed
  // Compare field index 1 id => now
}

/** Returns true if wrapped element has new fields to be added to the layout */
function hasNewFields(wrapperRefs: WrapperRefs, fields: FieldInfo[]): boolean {
  return !fields.every((field) => findByRefIds(wrapperRefs, field.refIds));
}

function shouldInjectFieldsToElement(element: CanvasElement | undefined): boolean {
  if (hasSearchAndFilters(element)) {
    return !!element?.props?.content?.searchAndFilters.injectToElement;
  }

  return false;
}

function shouldChangeInjectionMethod(
  draft: Draft<ElementsState>,
  elementId: CanvasElementId,
  fields: FieldInfo[],
): boolean {
  const element = getElement(draft, elementId);

  // Element has no props or is already using new injection method
  if (
    !hasProps(element) ||
    (hasSearchAndFilters(element) && element.props.content.searchAndFilters.injectToElement)
  ) {
    return false;
  }

  const wrapperRefs = getWrapperRefs(element);
  const wrappersCount = Object.keys(wrapperRefs).reduce((acc, key) => {
    return key === 'container' ? acc : acc + 1;
  }, 0);
  const fieldsCount = fields.length;

  return fieldsCount !== wrappersCount;
}

function moveSearchAndFiltersToElementSlotMutably(
  draft: Draft<EngineStateBase>,
  elementId: CanvasElementId,
): void {
  const element = getElement(draft, elementId);

  if (!element || !hasProps(element) || !hasSearchAndFilters(element)) {
    return;
  }

  const wrapperRefs = getWrapperRefs(element);
  const containerRef = wrapperRefs['container'];

  if (!containerRef) {
    return;
  }

  const containerElement = getElement(draft, containerRef);

  if (!containerElement) {
    return;
  }

  removeElementFromParentMutably(draft, containerElement.id);

  addSearchAndFilterElementSlotMutably(element);

  placeElementMutably(draft, {
    elementId: containerElement.id,
    atPath: {
      id: element.id,
      parentId: element.id,
      slot: SEARCH_AND_FILTERS_ELEMENT_SLOT_ID,
      index: 0,
      targetPosition: { type: 'top' },
    },
  });

  unwrapElementMutably(draft, elementId, false);
}

function unwrapElementMutably(
  draft: Draft<EngineStateBase>,
  elementId: CanvasElementId,
  clearWrapperData = true,
): void {
  const element = getElement(draft, elementId);
  const wrapperElementId = findWrapperForElement(draft, elementId);

  if (!element) {
    throw new Error('Incorrect Element');
  }

  if (wrapperElementId) {
    // Unwrap the main element and remove unused wrapper
    const wrapperElement = getElement(draft, wrapperElementId);
    if (!wrapperElement) {
      throw new Error('Could not find wrapper element');
    }

    // Revert the element's sizing to wrapper's sizing
    element.width = { ...wrapperElement.width };
    element.height = wrapperElement.height && { ...wrapperElement.height };

    // Store position of the wrapper
    const wrapperPath = getElementPath(draft.entities, wrapperElementId);
    if (!wrapperPath) {
      throw new Error(`Could not identify wrapper's path`);
    }
    wrapperPath.targetPosition = { type: 'left' };

    // Remove the mainElement before removing the wrapper
    removeElementFromParentMutably(draft, elementId);

    // Remove all elements inside of the wrapper
    removeElementMutably(draft, wrapperElementId);

    // Clear wrapper data
    if (clearWrapperData && hasProps(element)) {
      delete element.props.wrapper;
    }

    // Put the main element back to its position
    placeElementMutably(draft, { elementId, atPath: wrapperPath });

    // Select the main element
    draft.selectedPathNode = { id: elementId };
  }
}

function addSearchAndFilterElementSlotMutably(element: CanvasElement): void {
  if (element.slots?.[SEARCH_AND_FILTERS_ELEMENT_SLOT_ID]) {
    return;
  }

  if (element.slots?.ids) {
    element.slots.ids.push(SEARCH_AND_FILTERS_ELEMENT_SLOT_ID);
    element.slots[SEARCH_AND_FILTERS_ELEMENT_SLOT_ID] = { children: [] };
    return;
  }

  element.slots = {
    ids: [SEARCH_AND_FILTERS_ELEMENT_SLOT_ID],
    [SEARCH_AND_FILTERS_ELEMENT_SLOT_ID]: { children: [] },
  };
}

function createContainersForFieldsMutably(
  draft: Draft<ElementsState>,
  containerRef: CanvasElementId,
  fields: FieldInfo[],
  wrapperRefs: WrapperRefs,
  mainElementType: CanvasElementType,
): void {
  const fieldContainer = getElement(draft, containerRef);
  if (!isElementType(fieldContainer, 'container')) {
    throw new Error('Container error');
  }

  // Determine what field first column contains
  let columnContainsSearch = false;
  let hasSearch = false;
  let hasFilters = false;
  for (const field of fields) {
    if (hasSearch && hasFilters) {
      break;
    }

    const fieldRef = findByRefIds(wrapperRefs, field.refIds);
    const fieldElement = getElement(draft, fieldRef);

    // Make sure that element is in container managed by this mechanism
    const columnId =
      fieldElement?.parentNode?.id === containerRef ? fieldElement?.parentNode?.slot : undefined;

    if (field.type === 'search') {
      hasSearch = true;
      if (columnId) {
        columnContainsSearch = true;
      }
    } else {
      hasFilters = true;
    }
  }

  // Container has enough columns - do nothing
  if (fieldContainer.slots.ids.length < 2) {
    const existingColumnId = fieldContainer.slots.ids[0];

    // Search column exists need to setup filters column
    if (columnContainsSearch && hasFilters) {
      insertColumnMutably(draft, containerRef, existingColumnId, 'after-slot', {});
    } else if (!columnContainsSearch && hasSearch && hasFilters) {
      insertColumnMutably(draft, containerRef, existingColumnId, 'before-slot', {});
    }
  }

  const noPadding = { style: { container: { offset: { padding: '' } } } };
  const firstColumnId = fieldContainer.slots.ids[0];
  const lastColumnId = fieldContainer.slots.ids[fieldContainer.slots.ids.length - 1];

  if (hasSearch && firstColumnId) {
    const column = fieldContainer.slots[firstColumnId];
    if (column) {
      column.props = deepmergeExceptArrays(column.props, noPadding);
      column.sizing = { mode: 'fit-content' };
      if (column.alignment) {
        column.alignment.horizontal = 'left';
      }
    }
  }
  if (hasFilters && lastColumnId) {
    const column = fieldContainer.slots[lastColumnId];
    const horizontal: SlotAlignment['horizontal'] =
      mainElementType === 'dataFilter' && !hasSearch ? 'left' : 'right';
    if (!fieldContainer.slots[lastColumnId]?.children.length && column) {
      column.props = deepmergeExceptArrays(column.props, noPadding);
      column.sizing = undefined;
      column.alignment = { horizontal, vertical: 'top', syncVerticalLineAlignment: true };
    }
    if (column?.alignment) {
      column.alignment.horizontal = horizontal;
    }
  }

  // Make sure that there will not be a single column with fit-content mode
  if (fieldContainer.slots.ids.length === 1) {
    const column = fieldContainer.slots[fieldContainer.slots.ids[0]];
    if (column) {
      column.sizing = undefined;
    }
  }
}

function removeFieldTypeChangedMarkerMutably(
  mainElement: CanvasElement,
  filterFieldId: number,
): void {
  if (!hasSearchAndFilters(mainElement)) {
    return;
  }
  const filter = mainElement.props.content.searchAndFilters.filters?.find(
    (f) => f.id === filterFieldId,
  );
  delete filter?.__fieldTypeChanged;
}
